import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Home.module.css';
import { useState } from 'react';
import NewsletterSubscription from '../components/NewsletterSubscription';

export default function Home() {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleClick = (e, path) => {
    e.preventDefault();
    setIsNavigating(true);
    setTimeout(() => {
      navigate(path);
    }, 300);
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>
          MyPeopleDatabase | Modern Address Book, Contact Manager & Personal Knowledge Management
        </title>
        <meta
          name="description"
          content="MyPeopleDatabase is a modern address book and contact manager that also powers your personal knowledge management (PKM) for people and relationships, with smart groups, privacy, and data ownership."
        />
        <meta
          name="keywords"
          content="MyPeopleDatabase, personal knowledge management, PKM, contact manager, digital address book, relationship manager"
        />
      </Helmet>

      <div className={styles.overlay} />
      <div className={`${styles.content} ${isNavigating ? styles.fadeOut : ''}`}>
        <h1 className={styles.title}>
          Take back ownership of your people network
        </h1>
        <p className={styles.description}>
          A private desktop app for organizing contacts, notes, groups, and events without giving your data to the cloud.
        </p>

        <div className={styles.quickBenefits}>
          <div className={styles.benefit}>✔ Private local database</div>
          <div className={styles.benefit}>✔ Notes about every person</div>
          <div className={styles.benefit}>✔ Share groups securely</div>
        </div>

        <div className={styles.heroCtaRow}>
          <button
            onClick={e => handleClick(e, '/buy')}
            className={`${styles.button} ${styles.heroPrimaryCta} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Get MyPeopleDatabase
          </button>
          <button
            onClick={e => handleClick(e, '/download')}
            className={`${styles.button} ${styles.secondaryButton} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Free Download
          </button>
        </div>

        <div className={styles.trustBar}>
          <span className={styles.trustItem}>🔒 Data stays on your device</span>
          <span className={styles.trustItem}>🚫 No ads or tracking</span>
          <span className={styles.trustItem}>💳 Secure PayPal checkout</span>
          <span className={styles.trustItem}>💾 Free download to try first</span>
        </div>

        <div className={styles.fragmentationSection}>
          <h2 className={styles.fragmentationHeadline}>Where are your contacts right now?</h2>
          <p className={styles.fragmentationSubtext}>
            For most people, the answer is: scattered everywhere.
          </p>
          <div className={styles.fragmentationCloud}>
            <span className={styles.silo}>📱 Phone</span>
            <span className={styles.silo}>📧 Gmail</span>
            <span className={styles.silo}>💼 LinkedIn</span>
            <span className={styles.silo}>📘 Facebook</span>
            <span className={styles.silo}>📬 Outlook</span>
            <span className={styles.silo}>💬 iMessage / WhatsApp</span>
            <span className={styles.silo}>📇 Business cards</span>
            <span className={styles.silo}>🗒 Sticky notes</span>
            <span className={styles.silo}>📋 Old spreadsheets</span>
            <span className={styles.silo}>🧠 "I'll remember later"</span>
          </div>
          <div className={styles.fragmentationConclusion}>
            <div className={styles.fragmentationArrowDown}>↓</div>
            <p className={styles.fragmentationResult}>
              Someone important gets forgotten — and you feel it.
            </p>
            <p className={styles.fragmentationSolution}>
              MyPeopleDatabase is one place for all of them. On your device. Under your control.
            </p>
          </div>
        </div>

        <div className={styles.emotionalBenefitsPanel}>
          <h2 className={styles.emotionalBenefitsTitle}>Feel on top of your relationships again</h2>
          <p className={styles.emotionalBenefitsSubtitle}>
            Because the people you care about shouldn’t live in forgotten threads, scattered apps,
            and “I’ll remember later.”
          </p>
          <ul className={styles.emotionalBenefitsList}>
            <li>Stop feeling guilty about forgetting names, details, and follow-ups</li>
            <li>Show up more present—remember what matters when it matters</li>
            <li>Turn “we should catch up” into real momentum and consistency</li>
            <li>Feel calm knowing your people data is organized and under your control</li>
          </ul>
          <div className={styles.emotionalBenefitsCtaRow}>
            <button
              onClick={e => handleClick(e, '/buy')}
              className={`${styles.button} ${styles.emotionalBenefitsPrimaryCta} ${isNavigating ? styles.buttonFadeOut : ''}`}
            >
              Buy Now
            </button>
            <a
              href="/buy"
              onClick={e => handleClick(e, '/buy')}
              className={`${styles.link} ${isNavigating ? styles.linkFadeOut : ''}`}
            >
              See pricing and download details →
            </a>
          </div>
        </div>

        <div className={styles.announcementPanel}>
          <div className={styles.announcementIcon}>🚀</div>
          <div className={styles.announcementContent}>
            <h3>Now Live!</h3>
            <p>MyPeopleDatabase is officially available. Start organizing your contacts today!</p>
            <a
              href="/buy"
              onClick={e => handleClick(e, '/buy')}
              className={`${styles.announcementLink} ${isNavigating ? styles.linkFadeOut : ''}`}
            >
              Download your copy now →
            </a>
          </div>
        </div>
        <div className={styles.announcementPanel}>
          <div className={styles.announcementIcon}>✨</div>
          <div className={styles.announcementContent}>
            <h3>New Feature: Private Sharing for Groups</h3>
            <p>
              Learn how you can safely share contact details with specific groups of people while
              keeping full control of your data.
            </p>
            <a
              href="/private-sharing-groups"
              onClick={e => handleClick(e, '/private-sharing-groups')}
              className={`${styles.announcementLink} ${isNavigating ? styles.linkFadeOut : ''}`}
            >
              See how private group sharing works →
            </a>
          </div>
        </div>
        <div className={styles.announcementPanel}>
          <div className={styles.announcementIcon}>💬</div>
          <div className={styles.announcementContent}>
            <h3>Join the Community</h3>
            <p>Connect with other users on Facebook, X/Twitter, and Reddit.</p>
            <a
              href="/communities"
              onClick={e => handleClick(e, '/communities')}
              className={`${styles.announcementLink} ${isNavigating ? styles.linkFadeOut : ''}`}
            >
              Explore Communities →
            </a>
          </div>
        </div>
        <div className={styles.announcementPanel}>
          <div className={styles.announcementIcon}>🤝</div>
          <div className={styles.announcementContent}>
            <h3>For Networkers: Your Personal Relationship OS</h3>
            <p>
              Stop losing valuable connections. Learn how MyPeopleDatabase helps serious networkers
              organize contacts, remember details, and turn meetings into lasting relationships.
            </p>
            <a
              href="/for-networkers"
              onClick={e => handleClick(e, '/for-networkers')}
              className={`${styles.announcementLink} ${isNavigating ? styles.linkFadeOut : ''}`}
            >
              See how networkers use MPD →
            </a>
          </div>
        </div>
        <div className={styles.highlightBanner}>
          <h2>Your Relationships. Your Data. Your Control.</h2>
          <p>
            MyPeopleDatabase is built on the principle that you should own all your data and your
            relationships with the people you're connected to. No exploitation, no
            commoditization—just complete ownership.
          </p>
        </div>
        <div className={styles.featuresContainer}>
          <ul className={styles.featuresList}>
            <li>Complete digital address book on your workstation</li>
            <li>Advanced contact manager with custom groups and categories</li>
            <li>Secure and private data storage on your device</li>
            <li>Easy import and export capabilities for your existing contacts</li>
          </ul>
        </div>
        <div className={styles.buttonContainer}>
          <button
            onClick={e => handleClick(e, '/buy')}
            className={`${styles.button} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Get Started Now
          </button>
          <button
            onClick={e => handleClick(e, '/features')}
            className={`${styles.button} ${styles.secondaryButton} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Explore Features
          </button>
          <button
            onClick={e => handleClick(e, '/why')}
            className={`${styles.button} ${styles.secondaryButton} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Why Choose Us
          </button>
        </div>

        <div className={styles.foundersPromo}>
          <h3>Become a Founder Customer</h3>
          <p>
            Join a small circle of early supporters who help shape the direction of MyPeopleDatabase
            and unlock premium benefits along the way.
          </p>
          <button
            onClick={e => handleClick(e, '/founders')}
            className={`${styles.button} ${styles.foundersButton} ${isNavigating ? styles.buttonFadeOut : ''}`}
          >
            Explore the Founders Program
          </button>
        </div>
        <div className={styles.affiliateLink}>
          <a
            href="/affiliate-program"
            onClick={e => handleClick(e, '/affiliate-program')}
            className={`${styles.link} ${isNavigating ? styles.linkFadeOut : ''}`}
          >
            Join our Affiliate Reseller Program
          </a>
        </div>

        <NewsletterSubscription />
      </div>
    </div>
  );
}
